import React, { useState, useCallback, useEffect } from "react";
import {
  Card,
  Row,
  Col,
  Statistic,
  Select,
  DatePicker,
  Button,
  Space,
  Typography,
  Divider,
  Table,
  Tag,
  Empty,
  Spin,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import RobinPage from "../components/core/RobinPage";
import {
  useGetAllMyStoresQuery,
  useGetPartnerBalanceLazyQuery,
} from "../graphql/generated/hooks";
import {
  WalletOutlined,
  ShopOutlined,
  CalendarOutlined,
} from "@ant-design/icons";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// TypeScript interfaces
interface GetBalanceFilterDto {
  storeIds?: number[] | null;
  dateFrom?: Date | null;
  dateTo?: Date | null;
}

interface StoreBalance {
  balance: string;
  storeId: number;
  storeName: string;
  storeNameAr: string;
}

interface BalanceResponse {
  totalBalance: string;
  storeBalances: StoreBalance[];
}

function Balance() {
  const { t, i18n } = useTranslation();
  const { data: storesData, loading: storesLoading } = useGetAllMyStoresQuery();
  
  // Use the generated lazy query hook
  const [getPartnerBalance, { data: balanceData, loading, error }] =
    useGetPartnerBalanceLazyQuery({
      fetchPolicy: "network-only",
      errorPolicy: "all",
    });

  // State management
  const [filters, setFilters] = useState<GetBalanceFilterDto>({
    storeIds: null,
    dateFrom: null,
    dateTo: null,
  });

  const [hasSearched, setHasSearched] = useState(false);

  // Fetch balance data
  const fetchBalance = useCallback(() => {
    getPartnerBalance({
      variables: {
        filter: {
          storeIds: filters.storeIds,
          dateFrom: filters.dateFrom,
          dateTo: filters.dateTo,
        },
      },
    });
    setHasSearched(true);
  }, [getPartnerBalance, filters]);

  // Load data on component mount
  useEffect(() => {
    fetchBalance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle errors
  useEffect(() => {
    if (error) {

      // Check if it's a GraphQL error or network error
      if (error.graphQLErrors && error.graphQLErrors.length > 0) {
        // Handle GraphQL errors
        const gqlError = error.graphQLErrors[0];
        if (
          gqlError.message.includes("Cannot query field") ||
          gqlError.message.includes("Unknown")
        ) {
          message.warning(t("error.balance_not_implemented"));
        } else {
          message.error(gqlError.message || t("error.failed_to_fetch_balance"));
        }
      } else if (error.networkError) {
        // Handle network errors
        message.error(t("error.network_error"));
      } else {
        // Handle other errors
        message.error(error.message || t("error.failed_to_fetch_balance"));
      }
    }
  }, [error, t]);

  // Filter handlers
  const handleFilterChange = (key: keyof GetBalanceFilterDto, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFilters({
      storeIds: null,
      dateFrom: null,
      dateTo: null,
    });
    // Fetch with reset filters
    setTimeout(() => fetchBalance(), 0);
  };

  // Format currency
  const formatCurrency = (amount: string) => {
    const num = parseFloat(amount);
    const isNegative = num < 0;
    const absoluteAmount = Math.abs(num);

    return {
      value: absoluteAmount.toFixed(2),
      isNegative,
    };
  };

  // Table columns for store balances
  const columns: ColumnsType<StoreBalance> = [
    {
      title: t("label.balance.store_id"),
      dataIndex: "storeId",
      key: "storeId",
      width: 100,
      align: "center",
      render: (storeId: number) => (
        <Tag color="blue" icon={<ShopOutlined />}>
          {storeId}
        </Tag>
      ),
    },
    {
      title: t("label.balance.store_name"),
      dataIndex: i18n.language === "ar" ? "storeNameAr" : "storeName",
      key: "storeName",
      render: (_: any, record: StoreBalance) => {
        const name =
          i18n.language === "ar" ? record.storeNameAr : record.storeName;
        return (
          <Space>
            <ShopOutlined />
            <Text strong>{name || record.storeName || record.storeNameAr}</Text>
          </Space>
        );
      },
    },
    {
      title: t("label.balance.amount"),
      dataIndex: "balance",
      key: "balance",
      width: 200,
      align: "right",
      render: (balance: string) => {
        const { value, isNegative } = formatCurrency(balance);
        return (
          <Text
            strong
            style={{
              fontSize: "16px",
              color: isNegative ? "#ff4d4f" : "#52c41a",
            }}
          >
            {isNegative ? "-" : "+"}SAR {value}
          </Text>
        );
      },
      sorter: (a, b) => parseFloat(a.balance) - parseFloat(b.balance),
    },
  ];

  // Calculate total balance display
  const totalBalanceFormatted = balanceData?.getPartnerBalance
    ? formatCurrency(balanceData.getPartnerBalance.totalBalance)
    : null;

  return (
    <RobinPage title={t("label.menu.balance")}>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Filters Card */}
        <Card
          title={
            <Space>
              <CalendarOutlined />
              {t("label.filters")}
            </Space>
          }
          size="small"
        >
          <Row gutter={[16, 16]}>
            {/* Store Selection */}
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text type="secondary">{t("label.balance.stores")}</Text>
                <Select
                  mode="multiple"
                  placeholder={t("placeholder.select_stores")}
                  allowClear
                  style={{ width: "100%" }}
                  value={filters.storeIds || []}
                  onChange={(value) =>
                    handleFilterChange("storeIds", value.length > 0 ? value : null)
                  }
                  loading={storesLoading}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={storesData?.getAllStoresForPartner?.map((store) => ({
                    label: `${i18n.language === "ar" ? store.nameAr : store.name} (ID: ${store.id})`,
                    value: store.id,
                  }))}
                />
              </Space>
            </Col>

            {/* Date Range */}
            <Col xs={24} sm={12} md={8}>
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                <Text type="secondary">{t("label.balance.date_range")}</Text>
                <RangePicker
                  placeholder={[t("label.start_date"), t("label.end_date")]}
                  style={{ width: "100%" }}
                  value={
                    filters.dateFrom && filters.dateTo
                      ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
                      : null
                  }
                  onChange={(dates) => {
                    if (dates && dates[0] && dates[1]) {
                      handleFilterChange("dateFrom", dates[0].toDate());
                      handleFilterChange("dateTo", dates[1].toDate());
                    } else {
                      handleFilterChange("dateFrom", null);
                      handleFilterChange("dateTo", null);
                    }
                  }}
                />
              </Space>
            </Col>

            {/* Action Buttons */}
            <Col xs={24} sm={24} md={8}>
              <Space
                direction="vertical"
                style={{ width: "100%", marginTop: 22 }}
              >
                <Space wrap>
                  <Button
                    type="primary"
                    icon={<WalletOutlined />}
                    onClick={fetchBalance}
                    loading={loading}
                  >
                    {t("button.calculate_balance")}
                  </Button>
                  <Button onClick={resetFilters}>{t("button.reset_filters")}</Button>
                </Space>
              </Space>
            </Col>
          </Row>
        </Card>

        {/* Loading State */}
        {loading && (
          <Card>
            <div style={{ textAlign: "center", padding: "40px" }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">{t("loading")}</Text>
              </div>
            </div>
          </Card>
        )}

        {/* Balance Summary */}
        {!loading && hasSearched && balanceData?.getPartnerBalance && (
          <>
            {/* Total Balance Card */}
            <Card
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                borderRadius: "12px",
              }}
            >
              <Row gutter={16} align="middle">
                <Col xs={24} md={12}>
                  <Space direction="vertical" size="small">
                    <Space>
                      <WalletOutlined
                        style={{ fontSize: "32px", color: "white" }}
                      />
                      <Title level={4} style={{ margin: 0, color: "white" }}>
                        {t("label.balance.total_balance")}
                      </Title>
                    </Space>
                    {totalBalanceFormatted && (
                      <Title
                        level={1}
                        style={{
                          margin: 0,
                          color: "white",
                          fontSize: "48px",
                        }}
                      >
                        <span
                          style={{
                            color: totalBalanceFormatted.isNegative
                              ? "#ffccc7"
                              : "#b7eb8f",
                          }}
                        >
                          {totalBalanceFormatted.isNegative ? "-" : "+"}SAR {totalBalanceFormatted.value}
                        </span>
                      </Title>
                    )}
                  </Space>
                </Col>
                <Col xs={24} md={12}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Statistic
                        title={
                          <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                            {t("label.balance.total_stores")}
                          </Text>
                        }
                        value={balanceData.getPartnerBalance.storeBalances.length}
                        valueStyle={{ color: "white" }}
                        prefix={<ShopOutlined />}
                      />
                    </Col>
                    <Col span={12}>
                      <Statistic
                        title={
                          <Text style={{ color: "rgba(255,255,255,0.85)" }}>
                            {t("label.balance.positive_balances")}
                          </Text>
                        }
                        value={
                          balanceData.getPartnerBalance.storeBalances.filter(
                            (store) => parseFloat(store.balance) >= 0
                          ).length
                        }
                        valueStyle={{ color: "white" }}
                        prefix={<WalletOutlined />}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>

            <Divider />

            {/* Store Balances Table */}
            <Card
              title={
                <Space>
                  <ShopOutlined />
                  <Text strong style={{ fontSize: "16px" }}>
                    {t("label.balance.store_balances")}
                  </Text>
                </Space>
              }
            >
              <Table
                columns={columns}
                dataSource={balanceData.getPartnerBalance.storeBalances}
                rowKey="storeId"
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} ${t("label.of")} ${total} ${t("label.balance.stores")}`,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                locale={{
                  emptyText: (
                    <Empty
                      description={t("label.balance.no_stores")}
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ),
                }}
              />
            </Card>
          </>
        )}

        {/* Empty State */}
        {!loading && hasSearched && !balanceData?.getPartnerBalance && (
          <Card>
            <Empty
              description={t("label.balance.no_data")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        )}

        {/* Initial State */}
        {!loading && !hasSearched && (
          <Card>
            <Empty
              description={t("message.apply_filters_and_search")}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          </Card>
        )}
      </Space>
    </RobinPage>
  );
}

export default Balance;

